import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/components/shared/ThemeProvider";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Books from "@/pages/Books";
import ParticipateV2 from "@/pages/ParticipateV2";
import Work from "@/pages/Work";

import PublisherWorkflow from "@/pages/publisher/Workflow";
import PublisherDashboard from "@/pages/publisher/PublisherDashboard";
import PublicProfile from "@/pages/publisher/PublicProfile";

import NarratorDashboard from "@/pages/narrator/NarratorDashboard";
import NarratorOnboarding from "@/pages/narrator/NarratorOnboarding";
import NarratorDemos from "@/pages/narrator/NarratorDemos";
import NarratorProjects from "@/pages/narrator/NarratorProjects";
import ProjectWorkspace from "@/pages/narrator/ProjectWorkspace";
import PublicNarratorProfile from "@/pages/narrator/PublicNarratorProfile";

import PaymentEntry from "@/pages/payment/PaymentEntry";
import PaymentCheckout from "@/pages/payment/PaymentCheckout";
import PaymentHistory from "@/pages/payment/PaymentHistory";

import Inbox from "@/pages/Inbox";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminQueues from "@/pages/admin/AdminQueues";
import AdminBooks from "@/pages/admin/AdminBooks";
import AdminPublishers from "@/pages/admin/AdminPublishers";
import AdminNarrators from "@/pages/admin/AdminNarrators";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminWorkspaces from "@/pages/admin/AdminWorkspaces";

import authService from "@/services/authService";

function ProtectedRoute({ children }) {
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* User Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/books"
            element={
              <ProtectedRoute>
                <Books />
              </ProtectedRoute>
            }
          />
          <Route
            path="/participate"
            element={
              <ProtectedRoute>
                <ParticipateV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/work"
            element={
              <ProtectedRoute>
                <Work />
              </ProtectedRoute>
            }
          />

          {/* Publisher Routes */}
          <Route
            path="/publisher/workflow"
            element={
              <ProtectedRoute>
                <PublisherWorkflow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/publisher/dashboard"
            element={
              <ProtectedRoute>
                <PublisherDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/p/:slug" element={<PublicProfile />} />

          {/* Narrator Routes */}
          <Route
            path="/narrator"
            element={
              <ProtectedRoute>
                <NarratorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/narrator/onboarding"
            element={
              <ProtectedRoute>
                <NarratorOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/narrator/demos"
            element={
              <ProtectedRoute>
                <NarratorDemos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/narrator/projects"
            element={
              <ProtectedRoute>
                <NarratorProjects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/narrator/projects/:projectId"
            element={
              <ProtectedRoute>
                <ProjectWorkspace />
              </ProtectedRoute>
            }
          />

          {/* Public Narrator Profile */}
          <Route path="/n/:slug" element={<PublicNarratorProfile />} />

          {/* Payment Routes */}
          <Route path="/payment" element={<PaymentEntry />} />
          <Route path="/payment/:intentId" element={<PaymentCheckout />} />
          <Route path="/payments/history" element={<PaymentHistory />} />

          {/* Inbox Route */}
          <Route
            path="/inbox"
            element={
              <ProtectedRoute>
                <Inbox />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes - Protected by AdminLayout */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="queues" element={<AdminQueues />} />
            <Route path="workspaces" element={<AdminWorkspaces />} />
            <Route path="books" element={<AdminBooks />} />
            <Route path="publishers" element={<AdminPublishers />} />
            <Route path="narrators" element={<AdminNarrators />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          </Routes>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
